import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import './FaceDetection.css'
import alert from '../utils/alertService'

const FaceDetection = forwardRef(({ enabled, attemptId, onViolation, onAutoSubmit, onTotalViolationsChange, maxViolations = 3 }, ref) => {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const intervalRef = useRef(null)
  const stoppedRef = useRef(false) // Track if detection has been stopped to prevent race conditions
  const abortControllerRef = useRef(null) // AbortController for cancelling in-flight fetch requests
  const [isDetecting, setIsDetecting] = useState(false)
  const [faceCount, setFaceCount] = useState(null) // null = not yet detected
  const [isLooking, setIsLooking] = useState(null) // null = not yet detected
  const [faceVerified, setFaceVerified] = useState(null) // null = not yet checked
  const [error, setError] = useState('')
  const [cameraActive, setCameraActive] = useState(false)
  const [serviceAvailable, setServiceAvailable] = useState(true)
  const [isInitializing, setIsInitializing] = useState(true)
  // Progressive warning state
  const [warningLevel, setWarningLevel] = useState(0)
  const [totalViolations, setTotalViolations] = useState(0)
  const [remainingViolations, setRemainingViolations] = useState(maxViolations)
  const [autoSubmitImminent, setAutoSubmitImminent] = useState(false)
  const [warnings, setWarnings] = useState([])
  // Current issue state for clear user feedback
  const [currentIssue, setCurrentIssue] = useState(null)
  // Gaze details from backend for more specific messaging
  const [gazeDetails, setGazeDetails] = useState(null)
  // Track prohibited object detection from YOLO
  const [prohibitedObject, setProhibitedObject] = useState(null)
  // Track consecutive service failures for security
  const consecutiveFailuresRef = useRef(0)
  // Track last frontend violation logged to prevent duplicates (5 second cooldown)
  const lastFrontendViolationRef = useRef({ type: null, timestamp: 0 })
  const FRONTEND_VIOLATION_COOLDOWN_MS = 5000
  const SERVICE_FAILURE_THRESHOLD = 3 // Log violation after 3 consecutive failures
  // Debouncing for rapid state changes (screenshot/tab switch)
  const lastAnalysisTimeRef = useRef(0)
  const visibilityChangeTimeRef = useRef(0)
  const ANALYSIS_DEBOUNCE_MS = 1000 // Minimum time between analyses
  const VISIBILITY_GRACE_MS = 2000 // Grace period after visibility change

  // Helper to get issue details for user feedback
  const getIssueDetails = (issueType, details = null) => {
    const issues = {
      no_face: {
        icon: '👤',
        title: 'Face Not Visible',
        message: 'Please position your face in front of the camera',
        action: 'Make sure your face is clearly visible and well-lit',
        severity: 'high'
      },
      multiple_faces: {
        icon: '👥',
        title: 'Multiple People Detected',
        message: 'Only you should be visible during the test',
        action: 'Ask others to move out of the camera view',
        severity: 'high'
      },
      not_looking: {
        icon: '👀',
        title: 'Please Look at Screen',
        message: details || 'Keep your eyes on the screen while taking the test',
        action: 'Face the camera and look at your screen',
        severity: 'medium'
      },
      looking_at_phone: {
        icon: '📱',
        title: 'Looking Down - Possible Phone Use',
        message: 'You appear to be looking down at a device',
        action: 'Please put away any devices and focus on the test',
        severity: 'high'
      },
      looking_away: {
        icon: '↔️',
        title: 'Looking Away From Screen',
        message: details || 'Your head or eyes are turned away from the screen',
        action: 'Please face forward and look at your screen',
        severity: 'medium'
      },
      looking_up: {
        icon: '⬆️',
        title: 'Looking Up',
        message: 'You appear to be looking upward',
        action: 'Please look at your screen',
        severity: 'medium'
      },
      face_partially_hidden: {
        icon: '🙈',
        title: 'Face Partially Hidden',
        message: 'Part of your face is not visible',
        action: 'Ensure your entire face is visible to the camera',
        severity: 'medium'
      },
      face_mismatch: {
        icon: '🚫',
        title: 'Identity Verification Failed',
        message: 'Your face does not match the registered profile',
        action: 'Contact your instructor if this persists',
        severity: 'high'
      },
      image_quality: {
        icon: '📷',
        title: 'Image Quality Issue',
        message: details || 'Camera image quality is poor',
        action: 'Improve lighting or adjust camera position',
        severity: 'low'
      },
      service_error: {
        icon: '⚠️',
        title: 'Detection Service Issue',
        message: 'Face detection is temporarily unavailable',
        action: 'Please wait, the system will retry automatically',
        severity: 'medium'
      },
      // NEW: Prohibited object detection
      prohibited_object: {
        icon: '📱',
        title: 'Prohibited Object Detected',
        message: details || 'A phone or other prohibited item was detected',
        action: 'Please remove all devices from the camera view',
        severity: 'high'
      },
      // NEW: Mouth movement detection
      mouth_movement: {
        icon: '🗣️',
        title: 'Speaking Detected',
        message: details || 'Mouth movement consistent with speaking was detected',
        action: 'Please remain silent during the test',
        severity: 'medium'
      }
    }
    return issues[issueType] || null
  }

  // Helper function to log violations directly to backend when red card appears
  const logFrontendViolation = async (issueType, issue) => {
    // Check cooldown - prevent duplicate violations of same type within 5 seconds
    const now = Date.now()
    if (
      lastFrontendViolationRef.current.type === issueType &&
      now - lastFrontendViolationRef.current.timestamp < FRONTEND_VIOLATION_COOLDOWN_MS
    ) {
      return // Skip - same violation type within cooldown period
    }

    // Update last violation tracking
    lastFrontendViolationRef.current = { type: issueType, timestamp: now }

    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/face/log-frontend-violation', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attempt_id: attemptId,
          type: issueType,
          severity: issue.severity,
          message: issue.title + ': ' + issue.message
        })
      })

      if (response.ok) {
        const result = await response.json()
        if (result.logged && result.total_violations !== undefined) {
          setTotalViolations(result.total_violations)
          setRemainingViolations(Math.max(0, maxViolations - result.total_violations))
          // Sync to parent
          if (onTotalViolationsChange) {
            onTotalViolationsChange(result.total_violations)
          }
          // Check for auto-submit
          if (result.auto_submitted) {
            stopFaceDetection()
            alert.warning('Test has been auto-submitted due to multiple violations', 0)
            if (onAutoSubmit) {
              onAutoSubmit()
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to log frontend violation:', err)
    }
  }

  // Update current issue based on detection state
  useEffect(() => {
    if (!isDetecting || isInitializing || faceCount === null) {
      setCurrentIssue(null)
      return
    }

    let newIssue = null
    let issueType = null

    // Priority order: service error > prohibited object > no face > multiple faces > gaze issues
    if (!serviceAvailable) {
      newIssue = getIssueDetails('service_error')
      issueType = 'service_error'
    } else if (prohibitedObject && prohibitedObject.detected) {
      // Prohibited object (phone/book) detected by YOLO - highest priority
      newIssue = getIssueDetails('prohibited_object', `Detected: ${prohibitedObject.names}`)
      issueType = 'prohibited_object'
    } else if (faceCount === 0) {
      newIssue = getIssueDetails('no_face')
      issueType = 'no_face'
    } else if (faceCount > 1) {
      newIssue = getIssueDetails('multiple_faces')
      issueType = 'multiple_faces'
    } else if (isLooking === false) {
      // Use gaze details for more specific messaging
      const detailsLower = gazeDetails ? gazeDetails.toLowerCase() : ''
      if (detailsLower.includes('phone') || detailsLower.includes('looking down')) {
        newIssue = getIssueDetails('looking_at_phone')
        issueType = 'looking_at_phone'
      } else if (detailsLower.includes('looking up')) {
        newIssue = getIssueDetails('looking_up')
        issueType = 'looking_up'
      } else if (detailsLower.includes('away') || detailsLower.includes('left') || detailsLower.includes('right') || detailsLower.includes('turned')) {
        newIssue = getIssueDetails('looking_away', gazeDetails)
        issueType = 'looking_away'
      } else {
        newIssue = getIssueDetails('not_looking', gazeDetails)
        issueType = 'not_looking'
      }
    }

    setCurrentIssue(newIssue)

    // Log violation directly to backend when high severity issue (red card) appears
    if (newIssue && newIssue.severity === 'high' && issueType && attemptId) {
      logFrontendViolation(issueType, newIssue)
    }
  }, [faceCount, isLooking, serviceAvailable, isDetecting, isInitializing, gazeDetails, prohibitedObject, attemptId])

  useEffect(() => {
    if (enabled) {
      startFaceDetection()
    } else {
      stopFaceDetection()
    }

    return () => {
      stopFaceDetection()
    }
  }, [enabled])

  // Track visibility changes to debounce analysis during screenshot/tab switches
  useEffect(() => {
    const handleVisibilityChange = () => {
      visibilityChangeTimeRef.current = Date.now()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const startFaceDetection = async () => {
    try {
      setIsInitializing(true)
      stoppedRef.current = false // Reset stopped flag when starting

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: 640,
          height: 480,
          facingMode: 'user'
        }
      })

      if (videoRef.current && !stoppedRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
        setIsDetecting(true)
        setCameraActive(true)
        setIsInitializing(false)

        // Run first detection after a short delay to let camera warm up (2 seconds)
        setTimeout(() => {
          if (!stoppedRef.current) captureAndAnalyze()
        }, 2000)

        // Start periodic face detection - every 2 seconds for responsive feedback
        intervalRef.current = setInterval(() => {
          if (!stoppedRef.current) captureAndAnalyze()
        }, 2000)
      } else if (stoppedRef.current) {
        // Camera was stopped before initialization completed - clean up the stream
        stream.getTracks().forEach(track => track.stop())
      }
    } catch (err) {
      setError('Camera access denied or not available')
      setIsInitializing(false)
      console.error('Camera error:', err)
    }
  }

  const stopFaceDetection = () => {
    // Set stopped flag FIRST to prevent any pending/in-flight operations
    stoppedRef.current = true

    // Clear interval to stop any pending captures
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Stop all video tracks
    if (videoRef.current) {
      if (videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks()
        tracks.forEach(track => {
          try {
            track.stop()
          } catch (e) {
            console.warn('Error stopping track:', e)
          }
        })
        videoRef.current.srcObject = null
      }
      // Also pause the video element
      try {
        videoRef.current.pause()
      } catch (e) {
        // Ignore pause errors
      }
    }

    setIsDetecting(false)
    setCameraActive(false)
    // Reset other states
    setFaceCount(null)
    setIsLooking(null)
    setFaceVerified(null)
    setCurrentIssue(null)
    setProhibitedObject(null)
    setWarningLevel(0)
  }

  // Expose stopFaceDetection to parent component via ref
  useImperativeHandle(ref, () => ({
    stopCamera: stopFaceDetection
  }))

  const captureAndAnalyze = async () => {
    // Exit early if detection has been stopped (prevents race conditions)
    if (stoppedRef.current || !videoRef.current || !canvasRef.current) return

    // Debounce: Skip if document is hidden (screenshot/tab switch in progress)
    if (document.hidden) {
      return
    }

    // Debounce: Skip if we just had a visibility change (grace period)
    const timeSinceVisibilityChange = Date.now() - visibilityChangeTimeRef.current
    if (timeSinceVisibilityChange < VISIBILITY_GRACE_MS && visibilityChangeTimeRef.current > 0) {
      return
    }

    // Debounce: Skip if last analysis was too recent
    const timeSinceLastAnalysis = Date.now() - lastAnalysisTimeRef.current
    if (timeSinceLastAnalysis < ANALYSIS_DEBOUNCE_MS) {
      return
    }

    lastAnalysisTimeRef.current = Date.now()

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    // Set canvas size to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw current frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert canvas to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.8)
    const base64Data = imageData.split(',')[1]

    try {
      // Send to backend for face analysis
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/face/monitor-test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attempt_id: attemptId,
          image_data: base64Data
        })
      })

      if (response.ok) {
        const result = await response.json()
        const monitoringResult = result.monitoring_result

        setServiceAvailable(true)
        consecutiveFailuresRef.current = 0 // Reset failure counter on success

        // Update local state
        setFaceCount(monitoringResult.faces_detected)
        setIsLooking(monitoringResult.looking_at_camera)
        setFaceVerified(monitoringResult.face_verified)

        // Store gaze details for more specific issue messages
        if (monitoringResult.gaze_details) {
          setGazeDetails(monitoringResult.gaze_details)
        } else {
          setGazeDetails(null)
        }

        // Check for prohibited objects (phones, books) - track in state for useEffect
        if (monitoringResult.prohibited_object_detected && monitoringResult.prohibited_objects?.length > 0) {
          const objectNames = monitoringResult.prohibited_objects.map(obj => obj.object_type || 'prohibited item').join(', ')
          setProhibitedObject({ detected: true, names: objectNames })
        } else {
          setProhibitedObject(null)
        }

        // Update progressive warning state
        if (result.warning_level !== undefined) {
          setWarningLevel(result.warning_level)
        }
        if (result.total_violations !== undefined) {
          setTotalViolations(result.total_violations)
          setRemainingViolations(Math.max(0, maxViolations - result.total_violations))
          // Always sync total violations to parent component
          if (onTotalViolationsChange) {
            onTotalViolationsChange(result.total_violations)
          }
        }
        if (result.auto_submit_imminent !== undefined) {
          setAutoSubmitImminent(result.auto_submit_imminent)
        }
        if (result.warnings) {
          setWarnings(result.warnings)
        }

        // Handle confirmed violations - include total count for parent sync
        if (result.violations && result.violations.length > 0) {
          result.violations.forEach(violation => {
            onViolation({
              ...violation,
              total_violations: result.total_violations
            })
          })
        }

        // Auto-submit if test is flagged
        if (result.auto_submitted) {
          // Stop camera immediately when auto-submitted
          stopFaceDetection()
          alert.warning('Test has been auto-submitted due to multiple violations', 0)
          // Notify parent to handle the redirect
          if (onAutoSubmit) {
            onAutoSubmit()
          }
        }
      } else if (response.status === 503) {
        // Face detection service not available - SECURITY FIX: Don't assume face is present
        setServiceAvailable(false)
        consecutiveFailuresRef.current += 1

        // Set unknown state instead of assuming face is present
        setFaceCount(null)
        setIsLooking(null)

        // Log violation after consecutive failures (potential bypass attempt)
        if (consecutiveFailuresRef.current >= SERVICE_FAILURE_THRESHOLD) {
          onViolation({
            type: 'service_unavailable',
            severity: 'high',
            message: 'Face detection service unavailable - cannot verify identity',
            timestamp: new Date().toISOString()
          })
          consecutiveFailuresRef.current = 0 // Reset to avoid flooding
        }
      } else {
        // Handle other errors (like 500) - SECURITY FIX: Don't assume face is present
        console.error('Face detection API error:', response.status)
        const errorData = await response.json().catch(() => ({}))
        console.error('Error details:', errorData)
        setServiceAvailable(false)
        consecutiveFailuresRef.current += 1

        // Set unknown state instead of assuming face is present
        setFaceCount(null)
        setIsLooking(null)

        // Log violation after consecutive failures
        if (consecutiveFailuresRef.current >= SERVICE_FAILURE_THRESHOLD) {
          onViolation({
            type: 'detection_error',
            severity: 'medium',
            message: 'Face detection error - cannot verify identity',
            timestamp: new Date().toISOString()
          })
          consecutiveFailuresRef.current = 0
        }
      }
    } catch (err) {
      console.error('Face analysis error:', err)
      // On network error - SECURITY FIX: Don't assume face is present
      setServiceAvailable(false)
      consecutiveFailuresRef.current += 1

      // Set unknown state instead of assuming face is present
      setFaceCount(null)
      setIsLooking(null)

      // Log violation after consecutive failures
      if (consecutiveFailuresRef.current >= SERVICE_FAILURE_THRESHOLD) {
        onViolation({
          type: 'network_error',
          severity: 'medium',
          message: 'Network error during face detection - cannot verify identity',
          timestamp: new Date().toISOString()
        })
        consecutiveFailuresRef.current = 0
      }
    }
  }

  const getStatusColor = () => {
    if (!isDetecting) return '#718096'
    if (isInitializing) return '#4299e1' // blue for initializing
    if (!serviceAvailable) return '#e53e3e' // red when service unavailable - this is a security concern
    if (faceCount === null) return '#4299e1' // blue - waiting for first detection
    if (faceCount === 0) return '#e53e3e'
    if (faceCount > 1) return '#e53e3e'
    if (!isLooking) return '#f6ad55'
    return '#48bb78'
  }

  const getStatusText = () => {
    if (!isDetecting) return 'Detection Off'
    if (isInitializing) return 'Starting Camera...'
    if (!serviceAvailable) return 'Detection Unavailable - Action Required'
    if (faceCount === null) return 'Detecting...'
    if (faceCount === 0) return 'No Face Detected'
    if (faceCount > 1) return 'Multiple Faces'
    if (!isLooking) return 'Not Looking at Screen'
    return 'Monitoring Active'
  }

  const getWarningLevelColor = () => {
    switch (warningLevel) {
      case 0: return '#48bb78' // green - no issues
      case 1: return '#ecc94b' // yellow - minor issues
      case 2: return '#ed8936' // orange - moderate issues
      case 3: return '#e53e3e' // red - severe/imminent
      default: return '#718096'
    }
  }

  const getWarningLevelText = () => {
    switch (warningLevel) {
      case 0: return 'Good'
      case 1: return 'Caution'
      case 2: return 'Warning'
      case 3: return 'Critical'
      default: return '-'
    }
  }

  return (
    <div className="face-detection">
      <div className="detection-status">
        <div 
          className="status-indicator"
          style={{ backgroundColor: getStatusColor() }}
        />
        <span className="status-text">{getStatusText()}</span>
      </div>

      {enabled && (
        <div className="camera-container">
          <video
            ref={videoRef}
            className="camera-video"
            autoPlay
            muted
            playsInline
          />
          <canvas
            ref={canvasRef}
            className="detection-canvas"
            style={{ display: 'none' }}
          />
        </div>
      )}

      {error && (
        <div className="detection-error">
          {error}
        </div>
      )}

      {/* Current Issue Alert - Prominent user feedback */}
      {currentIssue && (
        <div className={`current-issue-alert severity-${currentIssue.severity}`}>
          <div className="issue-header">
            <span className="issue-icon">{currentIssue.icon}</span>
            <span className="issue-title">{currentIssue.title}</span>
          </div>
          <p className="issue-message">{currentIssue.message}</p>
          <p className="issue-action">{currentIssue.action}</p>
        </div>
      )}

      <div className="detection-info">
        <div className="info-item">
          <span className="info-label">Faces:</span>
          <span className="info-value">
            {faceCount === null ? '-' : faceCount}
          </span>
        </div>
        <div className="info-item">
          <span className="info-label">Looking:</span>
          <span className="info-value">
            {isLooking === null ? '-' : (isLooking ? 'Yes' : 'No')}
          </span>
        </div>
        <div className="info-item">
          <span className="info-label">Verified:</span>
          <span className={`info-value ${faceVerified === false ? 'verification-failed' : ''}`}>
            {faceVerified === null ? '-' : (faceVerified ? 'Yes' : 'No')}
          </span>
        </div>
      </div>

      {/* Current Status Section */}
      {enabled && serviceAvailable && (
        <div className="current-status-section">
          <h4 className="section-label">Current Status</h4>
          <div className="warning-level-container">
            <span className="warning-label">Level:</span>
            <div className="warning-level-bar">
              <div
                className="warning-level-fill"
                style={{
                  width: `${(warningLevel / 3) * 100}%`,
                  backgroundColor: getWarningLevelColor()
                }}
              />
            </div>
            <span
              className="warning-level-text"
              style={{ color: getWarningLevelColor() }}
            >
              {getWarningLevelText()}
            </span>
          </div>
        </div>
      )}

      {/* Accumulated Violations Section - Visually distinct */}
      {enabled && serviceAvailable && (
        <div className="accumulated-violations-section">
          <h4 className="section-label">Session History</h4>
          <div className="violations-counter">
            <span className="violations-label">Violations:</span>
            <span className={`violations-value ${totalViolations > 0 ? 'has-violations' : ''}`}>
              {totalViolations} / {maxViolations}
            </span>
          </div>
          {totalViolations > 0 && (
            <p className="violations-explainer">
              Violations accumulate when issues persist for multiple frames.
            </p>
          )}
        </div>
      )}

      {/* Auto-submit imminent warning */}
      {autoSubmitImminent && remainingViolations <= 1 && (
        <div className="auto-submit-warning">
          <span className="warning-icon">⚠️</span>
          <span>
            {remainingViolations === 0
              ? 'Test will be auto-submitted!'
              : `Warning: Only ${remainingViolations} violation remaining before auto-submit`}
          </span>
        </div>
      )}

      {/* Preliminary warnings */}
      {warnings.length > 0 && (
        <div className="preliminary-warnings">
          {warnings.map((warning, index) => (
            <div key={index} className="preliminary-warning-item">
              <span className="warning-dot" />
              <span>{warning.message}</span>
            </div>
          ))}
        </div>
      )}

      {!serviceAvailable && cameraActive && (
        <div className="service-unavailable-notice service-unavailable-warning">
          Face detection service is unavailable. Your identity cannot be verified.
          Please contact your instructor if this persists.
          Continued failures may be logged as violations.
        </div>
      )}

      <div className="detection-instructions">
        <h4>Instructions:</h4>
        <ul>
          <li>Keep your face visible in the camera</li>
          <li>Look at the screen while taking the test</li>
          <li>Ensure no one else is in the frame</li>
          <li>Do not switch tabs or minimize the window</li>
        </ul>
      </div>
    </div>
  )
})

export default FaceDetection